import {
  ArrowLeft,
  Copy,
  ExternalLink,
  Link,
  Plus,
  Trash2,
} from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useHealthPassCard } from "../../hooks/useHealthPassCard";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import type { CardShare } from "../../services/api/cardService";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";

const ShareManagementPage: React.FC = () => {
  const { t } = useI18nStore();
  const navigate = useNavigate();
  const { shares, isSharesLoading, createShare, revokeShare } =
    useHealthPassCard();

  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleRevoke = async (share: CardShare) => {
    if (!window.confirm(t.cardxRevokeShareConfirm)) return;
    try {
      await revokeShare(share.id);
      toast.success(t.shareRevoked);
    } catch {
      toast.error(t.cardxRevokeShareFailed);
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success(t.shareCopied);
  };

  const activeShares = shares.filter(
    (s) => !s.revokedAt && new Date(s.expiresAt) > new Date(),
  );
  const expiredShares = shares.filter(
    (s) => s.revokedAt || new Date(s.expiresAt) <= new Date(),
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/traveler/card")}
            aria-label={t.back}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">{t.cardShares}</h1>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t.createShareLink}
        </button>
      </div>

      {isSharesLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Active shares */}
          {activeShares.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <Link className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{t.noSharesYet}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide px-1">
                {t.active}
              </h2>
              {activeShares.map((share) => (
                <ShareCard
                  key={share.id}
                  share={share}
                  onCopy={handleCopy}
                  onRevoke={handleRevoke}
                  t={t}
                />
              ))}
            </div>
          )}

          {/* Expired/revoked */}
          {expiredShares.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wide px-1">
                {t.cardxExpiredRevoked}
              </h2>
              {expiredShares.map((share) => (
                <ShareCard
                  key={share.id}
                  share={share}
                  onCopy={handleCopy}
                  onRevoke={handleRevoke}
                  t={t}
                  inactive
                />
              ))}
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <CreateShareModal
          onClose={() => setShowCreateModal(false)}
          onShare={createShare}
          t={t}
        />
      )}
    </div>
  );
};

// ── Share Card ────────────────────────────────────────────────────────────────

interface ShareCardProps {
  share: CardShare;
  onCopy: (url: string) => void;
  onRevoke: (share: CardShare) => void;
  t: Translation;
  inactive?: boolean;
}

const ShareCard: React.FC<ShareCardProps> = ({
  share,
  onCopy,
  onRevoke,
  t,
  inactive,
}) => {
  const fmt = useLocaleFormat();
  const isRevoked = !!share.revokedAt;
  const isExpired = new Date(share.expiresAt) <= new Date();

  return (
    <div
      className={`bg-white rounded-xl border p-4 space-y-2 ${inactive ? "opacity-60 border-gray-100" : "border-gray-200 shadow-sm"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-xs text-gray-500 truncate">
            {share.shareUrl}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <span>
              {t.cardxExpiresOn.replace("{date}", fmt.date(share.expiresAt))}
            </span>
            <span>
              {t.shareAccessCount} {share.accessCount} {t.shareTimes}
            </span>
            {share.hasPassword && <span>🔒</span>}
            {isRevoked && (
              <span className="text-red-500">{t.cardxStatusRevoked}</span>
            )}
            {!isRevoked && isExpired && (
              <span className="text-orange-500">{t.expired}</span>
            )}
          </div>
        </div>
        {!inactive && (
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => onCopy(share.shareUrl)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title={t.cardxCopyLink}
              aria-label={t.cardxCopyLink}
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
            <a
              href={share.shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={t.cardxOpenLink}
              aria-label={t.cardxOpenLink}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-gray-500" />
            </a>
            <button
              onClick={() => onRevoke(share)}
              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
              title={t.revokeShare}
              aria-label={t.revokeShare}
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Create Modal ──────────────────────────────────────────────────────────────

interface CreateShareModalProps {
  onClose: () => void;
  onShare: (
    expiry: "24h" | "72h" | "7d",
    password?: string,
  ) => Promise<CardShare>;
  t: Translation;
}

const CreateShareModal: React.FC<CreateShareModalProps> = ({
  onClose,
  onShare,
  t,
}) => {
  const [expiry, setExpiry] = useState<"24h" | "72h" | "7d">("72h");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const share = await onShare(expiry, password || undefined);
      setCreatedUrl(share.shareUrl);
      toast.success(t.shareCreated);
    } catch {
      toast.error(t.cardxCreateShareFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!createdUrl) return;
    navigator.clipboard.writeText(createdUrl);
    toast.success(t.shareCopied);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <h3 className="font-bold text-lg text-gray-800 mb-4">
          {t.createShareLink}
        </h3>

        {!createdUrl ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.shareExpiry}
              </label>
              <div className="flex gap-2">
                {(["24h", "72h", "7d"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setExpiry(opt)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      expiry === opt
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt === "24h"
                      ? t.shareExpiry24h
                      : opt === "72h"
                        ? t.shareExpiry72h
                        : t.shareExpiry7d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.sharePassword}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.cardxSharePasswordPlaceholder}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleCreate}
                disabled={loading}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? t.loading : t.createShareLink}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{t.shareCreated}</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 break-all text-xs text-gray-800 font-mono">
              {createdUrl}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-1.5"
              >
                <Copy className="w-4 h-4" />
                {t.cardxCopyLink}
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t.close}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareManagementPage;
